import React, { useState } from 'react'
import { Button, Modal, message } from 'antd'
import { ExclamationCircleFilled } from '@ant-design/icons'

import type { LinkedAccountItem } from '../../types/linkedAccount'
import { deleteLinkedAccount } from '../../apis/linkedAccountApis'
import { isForbiddenError, isNotFoundError } from '../../util/linkedAccount/linkedAccountErrors'
import { accountTypeBadgeLabel } from '../../util/linkedAccount/linkedAccountDisplay'
import { useIsMobile } from '../../hook/useMediaQuery'
import {
  LINKED_ACCOUNT_CONFIRM_MODAL_WIDTH_PX,
  LINKED_ACCOUNT_DIALOG_CONFIRM_CLASS,
  LINKED_ACCOUNT_SHORT_MODAL_WIDTH_PX,
} from '../../constants/linkedAccount'

/**
 * Hộp thoại "Xác nhận xoá tài khoản liên kết?" (SP-005, Task 7, `FR-W-04`) — Figma `26:9556`,
 * mobile `26:45216`.
 *
 * 🔴 BRD `FR-W-04` đòi hộp thoại NÊU RÕ loại và tên tài khoản sắp xoá; Figma không vẽ dòng đó.
 * BRD thắng ở điểm này (chống xoá nhầm, không phải trang trí) — dòng `<badge loại> · <username>`
 * lấy thẳng từ `item` ĐÃ CÓ SẴN trong tay (không gọi thêm API nào).
 *
 * 🔴 404 khi xoá nghĩa là bản ghi ĐÃ bị xoá ở nơi khác (một tab khác, một người khác) — coi như
 * thành công: đóng + `onDeleted()` để nơi gọi tải lại danh sách, KHÔNG hiện lỗi (không có gì để
 * báo lỗi, kết quả mong muốn — bản ghi biến mất — đã đúng).
 *
 * DOM hook cho bộ test trình duyệt / `LinkedAccountTab` (Task 7):
 * - `.delete-linked-account-modal` — className của `Modal` (render qua PORTAL vào `document.body`).
 * - `.delete-linked-account-modal__target` — dòng `"<badge loại> · <username>"`.
 * - `.delete-linked-account-modal__cancel-btn` / `__submit-btn` — hai nút footer (`Huỷ` / `Xoá`,
 *   nút `Xoá` là nút `danger`).
 *
 * 🔴 Responsive ≤767px (SP-005, Task 9): hộp thoại NGẮN (BRD §10) — GIỮ modal căn giữa, đổi `width`
 * từ `480` sang `LINKED_ACCOUNT_SHORT_MODAL_WIDTH_PX` (343) qua `useIsMobile()`, KHÔNG toàn màn.
 */

export interface DeleteLinkedAccountModalProps {
  /** `null` = đóng. */
  item: LinkedAccountItem | null
  onCancel: () => void
  /** Gọi sau khi xoá thành công HOẶC 404 (coi như đã xoá) — nơi gọi tải lại danh sách. */
  onDeleted: () => void
}

const MODAL_TITLE = 'Xác nhận xoá tài khoản liên kết?'
const MODAL_DESCRIPTION = 'Tài khoản liên kết này sẽ không còn hiển thị trong danh sách. Vui lòng xác nhận.'
const CANCEL_LABEL = 'Huỷ'
const DELETE_LABEL = 'Xoá'

const FORBIDDEN_MESSAGE = 'Bạn không có quyền thực hiện thao tác này.'
/**
 * Câu lỗi chung cho nhánh KHÁC 403/404 — brief chỉ chốt chữ cho 403 (`FORBIDDEN_MESSAGE`) và ứng
 * xử của 404 (coi như đã xoá); câu này là lựa chọn của người viết cho nhánh còn lại (500/mạng…),
 * theo đúng khuôn `AddLinkedAccountModal`/`EditLinkedAccountModal` (`GENERIC_ERROR_MESSAGE`) —
 * không để người dùng bấm Xoá xong màn hình im lặng không phản hồi gì.
 */
const GENERIC_ERROR_MESSAGE = 'Không xoá được tài khoản liên kết. Vui lòng thử lại.'

const DeleteLinkedAccountModal = ({ item, onCancel, onDeleted }: DeleteLinkedAccountModalProps): JSX.Element => {
  // 🔴 Fix round 1 (reviewer, Minor): component KHÔNG unmount giữa các bản ghi (container luôn
  // render nó, chỉ đổi `item`) ⇒ nút `Huỷ` phải KHOÁ trong lúc `submitting` — bấm Huỷ giữa lúc
  // lời gọi đang bay không huỷ được lời gọi đó, nó vẫn về VÀ vẫn gọi `onDeleted`/`onCancel` sau
  // khi hộp đã đóng; nếu hộp mở lại ngay cho một bản ghi khác trong lúc đó, `submitting` có thể
  // kẹt ở `true` (mất cơ hội bấm Xoá) hoặc callback của lượt cũ chạy nhầm ngữ cảnh bản ghi mới.
  const [submitting, setSubmitting] = useState(false)
  const isMobile = useIsMobile()

  const handleDelete = async (): Promise<void> => {
    if (item === null || submitting) return
    setSubmitting(true)
    try {
      await deleteLinkedAccount(item.id)
      onDeleted()
      onCancel()
    } catch (error) {
      if (isNotFoundError(error)) {
        // Coi như đã xoá — danh sách sẽ tự đúng sau khi tải lại, không có gì để báo lỗi.
        onDeleted()
        onCancel()
      } else if (isForbiddenError(error)) {
        message.error(FORBIDDEN_MESSAGE)
      } else {
        message.error(GENERIC_ERROR_MESSAGE)
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      className={`${LINKED_ACCOUNT_DIALOG_CONFIRM_CLASS} delete-linked-account-modal`}
      open={item !== null}
      width={isMobile ? LINKED_ACCOUNT_SHORT_MODAL_WIDTH_PX : LINKED_ACCOUNT_CONFIRM_MODAL_WIDTH_PX}
      maskClosable={false}
      destroyOnClose
      // Figma `26:9714`: hộp xác nhận không có nút × và không có vạch header — đóng bằng `Huỷ`/Esc.
      closable={false}
      onCancel={onCancel}
      footer={[
        <Button
          key="cancel"
          size="large"
          className="delete-linked-account-modal__cancel-btn"
          disabled={submitting}
          onClick={onCancel}
        >
          {CANCEL_LABEL}
        </Button>,
        <Button
          key="submit"
          danger
          type="primary"
          size="large"
          className="delete-linked-account-modal__submit-btn"
          loading={submitting}
          disabled={submitting}
          onClick={handleDelete}
        >
          {DELETE_LABEL}
        </Button>,
      ]}
    >
      <div className="delete-linked-account-modal__content">
        <ExclamationCircleFilled className="delete-linked-account-modal__icon" aria-hidden />
        <div className="delete-linked-account-modal__text">
          <p className="delete-linked-account-modal__title">{MODAL_TITLE}</p>
          <p className="delete-linked-account-modal__description">{MODAL_DESCRIPTION}</p>
          {item !== null && (
            <p className="delete-linked-account-modal__target">
              {`${accountTypeBadgeLabel(item)} · `}
              {/* Tên tách span riêng để tên dài không dấu cách ngắt ngay sau "loại ·" thay vì rơi cả
                  khối xuống dòng dưới (textContent vẫn nguyên "<loại> · <tên>"). */}
              <span className="delete-linked-account-modal__target-username">{item.username}</span>
            </p>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default DeleteLinkedAccountModal
